/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useRef, useEffect } from 'react';
import api from '../utils/api';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');
const isPaula = user.email === 'paula@commonwealthtransport.com';

export default function AskRosie() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: isPaula
      ? `Hey Paula! I'm ready to help dispatch. Tell me what's coming in and we'll figure it out together. I'll learn from every decision you make.`
      : `Hey${user.firstName ? ' ' + user.firstName : ''}! I'm Rosie. Ask me anything about today's schedule, drivers, trips — or report an issue and I'll log it for Paula.`
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setLoading(true);

    try {
      const apiMessages = updated
        .filter((m, i) => i > 0 || m.role === 'user')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await api.post('/admin-chat', {
        messages: apiMessages,
        userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'unknown',
        userRole: user.role || 'staff',
        userEmail: user.email || '',
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Try again.' }]);
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e0e0db' }}>
        <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>{isPaula ? 'Dispatch Partner' : 'Ask Rosie'}</h1>
        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{isPaula ? 'Talk through dispatch decisions — I\'m learning from you.' : 'Questions, issues, or just need help? I\'m here.'}</div>
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 8,
          }}>
            <div style={{
              maxWidth: '85%',
              padding: '8px 12px',
              borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user' ? '#185FA5' : '#f5f5f3',
              color: msg.role === 'user' ? 'white' : '#333',
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div style={{ padding: '8px 12px', borderRadius: '14px 14px 14px 4px', background: '#f5f5f3', color: '#999', fontSize: 13 }}>
              Thinking...
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '8px 12px', borderTop: '0.5px solid #e0e0db', background: 'white' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder={isPaula ? "Tell me what's coming in or ask who to send..." : "Ask a question or report an issue..."}
            style={{
              flex: 1, fontSize: 14, padding: '10px 14px',
              border: '0.5px solid #e0e0db', borderRadius: 20, outline: 'none',
            }}
            autoFocus
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            style={{
              padding: '8px 16px', borderRadius: 20,
              background: input.trim() && !loading ? '#185FA5' : '#e0e0db',
              color: 'white', border: 'none', fontSize: 13, fontWeight: 600,
              cursor: input.trim() && !loading ? 'pointer' : 'default',
            }}
          >
            Send
          </button>
        </div>
        <div style={{ fontSize: 10, color: '#bbb', textAlign: 'center', marginTop: 4 }}>
          Report problems and they'll be logged for the admin team.
        </div>
      </div>
    </div>
  );
}
